/* =====================================================================
 * rehypeGuideFaq — "질문형 소제목" 을 펼치고 접는 FAQ 카드로 묶는다.
 *
 * 표기 약속: `### 파일이 첨부되지 않을 땐 어떻게 하나요?` 처럼 물음표로 끝나는
 * 소제목(h3)은 질문이다. 그 아래부터 다음 제목(h2/h3) 전까지가 답이다.
 * 이 묶음을 `<details data-faq>` 로 감싸고, 질문은 `<summary>` 로 옮긴다.
 *
 * 물음표로 끝나지 않는 일반 소제목은 건드리지 않는다.
 * ===================================================================== */

import type { Element, ElementContent, Root, RootContent } from 'hast';
import { toText } from 'hast-util-to-text';

const isElement = (node: RootContent | ElementContent): node is Element => node.type === 'element';

const isHeading = (node: RootContent, tags: readonly string[]) => isElement(node) && tags.includes(node.tagName);

export function isFaqQuestion(node: RootContent): node is Element {
  return isHeading(node, ['h3']) && toText(node as Element).trim().endsWith('?');
}

export function rehypeGuideFaq() {
  return (tree: Root) => {
    const next: RootContent[] = [];
    let index = 0;
    while (index < tree.children.length) {
      const node = tree.children[index];
      if (!isFaqQuestion(node)) {
        next.push(node);
        index += 1;
        continue;
      }

      const answer: ElementContent[] = [];
      index += 1;
      while (index < tree.children.length && !isHeading(tree.children[index], ['h2', 'h3'])) {
        answer.push(tree.children[index] as ElementContent);
        index += 1;
      }
      next.push({
        type: 'element',
        tagName: 'details',
        properties: { dataFaq: true },
        children: [
          { type: 'element', tagName: 'summary', properties: {}, children: node.children },
          { type: 'element', tagName: 'div', properties: { dataFaqAnswer: true }, children: answer },
        ],
      });
    }
    tree.children = next;
  };
}
